import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { CourseProgressCategoriesData } from 'teacher-ui-client/components/tables/course-progress/categories'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface CourseProgressCategoriesResponse {
  course_progress_categories: [
    { category: 'literal'; average_score: Nullable<number>; questions_answered: Nullable<number> },
    { category: 'inferential'; average_score: Nullable<number>; questions_answered: Nullable<number> },
    { category: 'vocabulary_and_usage'; average_score: Nullable<number>; questions_answered: Nullable<number> },
    {
      category: 'text_analysis_and_critical_literacy'
      average_score: Nullable<number>
      questions_answered: Nullable<number>
    },
  ]
}

export async function courseProgressCategories(
  this: StudentEventsService,
  product: LegacyProductRex,
  precinct: LegacyPrecinctMyLessons,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<CourseProgressCategoriesData[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('course_progress_categories', product, statsScope)
  const query = this.buildQuery(statsScope, period, { precinct })

  const data = await this.fetchData<CourseProgressCategoriesResponse>(url, query)
  if (!data) return null

  const order = ['literal', 'inferential', 'vocabulary_and_usage', 'text_analysis_and_critical_literacy']
  const { course_progress_categories } = data

  const hasNoData = course_progress_categories.every((category) => {
    if (category.average_score === null && category.questions_answered === null) return true
    return false
  })
  if (hasNoData) return null

  // Sort the data by the categories, as they appear in the order array above.
  return [...course_progress_categories]
    .sort((a, b) => {
      const aIndex = order.indexOf(a.category)
      const bIndex = order.indexOf(b.category)
      if (aIndex > bIndex) return 1
      if (aIndex < bIndex) return -1
      return 0
    })
    .map((category) => ({
      ...category,
      average_score: category.average_score ?? null,
      questions_answered: category.questions_answered ?? 0,
    }))
}
